import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import CustomInput from '../../common/CustomInput/CustomInput';
import { Icon } from '../../common/Icon/Icon';
import Loader from '../../common/Loader/Loader';
import Pager from '../../common/Pager/Pager';
import TableHeader from '../../common/TableHeader/TableHeader';
import { useUserContext } from '../../core/context/user.context';
import useFetch from '../../core/hooks/useFetch';
import { useFormInput } from '../../core/hooks/useFormInput';

const BASE_URL = process.env.REACT_APP_SC_API_BASE;
const FILTERS = ['id', 'email', 'hotel', 'status'];
// TODO: adjust sort values to BE
const COLS: {
  label: string;
  sort?: string;
  align?: string;
}[] = [
  {
    label: 'Id',
    sort: 'id',
  },
  {
    label: 'User',
    sort: 'email',
  },
  {
    label: 'Hotel',
    sort: 'supplier.name',
  },
  {
    label: 'Status',
    sort: 'status',
  },
  {
    label: 'Created',
    sort: 'created',
  },
];

function Bookings() {
  const { user } = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const timeout = useRef<ReturnType<typeof setInterval> | null>(null);
  const page = useMemo(() => searchParams.get('page') || '1', [searchParams]);
  const searchValue = useMemo(() => searchParams.get('search') || '', [searchParams]);
  const search = useFormInput(searchValue || '');
  const sortValue = useMemo(() => searchParams.get('sort') || '', [searchParams]);
  const [sort, setSort] = useState<{
    property: string;
    order: boolean;
    changed: boolean;
  }>({
    property: sortValue.split('-')[0] || '',
    order: sortValue.split('-')[1] !== 'DESC',
    changed: !!sortValue,
  });
  const { result, doFetch, loading, error } = useFetch(
    `${BASE_URL}/admin/bookings?per_page=12&page=${page}${
      sort.property ? `&sort=${sort.property}${sort.order ? ':ASC' : ':DESC'}` : ''
    }${!search.value ? '' : FILTERS.reduce((str, value) => `${str}&${value}=${search.value}`, '')}`,
  );

  useEffect(() => {
    doFetch({
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    });
  }, [searchParams]);

  const downloadCSV = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/bookings/csv`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `bookings_${new Date().toLocaleString('en-gb', {
        day: '2-digit',
        month: 'numeric',
        year: 'numeric',
      })}.csv`;
      a.click();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.log(err.message);
    }
  }, []);

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      if (search.value) {
        searchParams.set('search', search.value);
      } else {
        searchParams.delete('search');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
    }, 500);
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [search.value]);

  useEffect(() => {
    if (sort.property && sort.changed) {
      searchParams.set('sort', `${sort.property}${sort.order ? '-ASC' : '-DESC'}`);
    } else {
      searchParams.delete('sort');
    }
    searchParams.delete('page');
    setSearchParams(searchParams);
  }, [sort]);

  const changeSort = useCallback(
    (val: string | undefined) => {
      if (val === sort.property)
        return setSort((prevState) => ({
          ...prevState,
          order: !prevState.order,
          changed: true,
        }));
      return setSort(() => ({
        property: val || '',
        order: true,
        changed: true,
      }));
    },
    [sort],
  );

  return (
    <section>
      <div className="wrap">
        <div className="table-header">
          <CustomInput id="search" name="search" placeholder="Search" input={search} />
          <button className="btn btn--blue" type="button" onClick={downloadCSV}>
            CSV
            <Icon.Download className="icon-small icon-right" />
          </button>
        </div>
        {!result ? (
          loading ? (
            <Loader size={32} bgColor="#f2fbfd" />
          ) : error ? (
            <div>{error.message}</div>
          ) : (
            <div className="fade-in">No Bookings found!</div>
          )
        ) : (
          <div className="table-container">
            <table>
              <TableHeader cols={COLS} sort={sort} changeSort={changeSort} />
              <tbody>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {result.data?.items.map((el: any) => (
                  <tr key={el._id}>
                    <td>
                      <Link to={`${el._id}`}>{el._id}</Link>
                    </td>
                    <td>{el.user_details.email}</td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {el.supplier.name}
                    </td>
                    <td>{el.status}</td>
                    <td>
                      {new Date(el.created).toLocaleString('en-gb', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {result?.data?.items?.length && result?.data?.pager?.total > 1 ? (
          <Pager total={result?.data?.pager.total} current={+page} />
        ) : null}
      </div>
    </section>
  );
}

export default Bookings;
